
import { mapState, mapActions, mapGetters } from 'vuex';
import { deparam } from '@/lib/utils';
import _ from '@/lib/lodash';

export default {
  i18nComponentKey: 'WidgetProductReviewsStatistics.components.StatisticsBase',
  data: () => ({
    activeTabIndex: 0,
    isLoadingStatistics: true
  }),
  computed: {
    ...mapState('window', ['windowService']),
    ...mapState('statistics', ['statistics']),
    ...mapState('widget', ['widget', 'noItemActionRequired']),
    ...mapGetters('statistics', ['itemsCount']),
    isLoading() {
      return this.isLoadingStatistics;
    },
    hasEvaluationTypes() {
      return (
        this.statistics.length > 0 &&
        this.statistics[0].evaluation_types.length > 0
      );
    },
    tabNames() {
      return _.map(this.statistics, 'user_property_type_option_name');
    },
    activeTab() {
      return {
        reviewCount: this.getReviewCount(this.activeTabIndex),
        userPropertyTypeOptionName: this.statistics[this.activeTabIndex]
          .user_property_type_option_name
      };
    },
    statisticsData() {
      return this.statistics[this.activeTabIndex].evaluation_types.map(
        evaluation_type => {
          const count = _.map(evaluation_type.options, 'count');
          const count_sum = _.sumBy(count);
          const percentages =
            count_sum === 0
              ? count
              : this.largestRemainderRound(
                  count.map(c => Math.round((c / count_sum) * 10000) / 100),
                  100
                );
          const indexOfMaxPercentage = _.indexOf(
            percentages,
            _.maxBy(percentages)
          );

          return {
            name: evaluation_type.name,
            data: evaluation_type.options.map((option, i) => ({
              name: option.name,
              value: option.value,
              percentage: percentages[i]
            })),
            topRatedOption: {
              ...evaluation_type.options[indexOfMaxPercentage],
              percentage: percentages[indexOfMaxPercentage]
            }
          };
        }
      );
    },
    totalReviewCount() {
      return this.getReviewCount(0);
    },
    isTabVisible() {
      return this.tabNames.length > 1;
    },
    isWidgetVisible() {
      return this.hasEvaluationTypes && !this.noItemActionRequired;
    },
    hasNoItemImage() {
      return (
        this.noItemActionRequired &&
        this.widget.no_item_action_type === 'image' &&
        this.widget.no_item_image_url
      );
    },
    statisticsTitle() {
      return this.$t('statistics.content_title_html', this.activeTab);
    }
  },
  created() {
    this.windowService.addMessageHandler(this.handleMessage);
  },
  mounted() {
    this.fetchStatistics().finally(() => (this.isLoadingStatistics = false));
  },
  methods: {
    ...mapActions('statistics', ['fetchStatistics']),
    ...mapActions('widget', ['fetchWidget']),
    ...mapActions('session', ['setSessionParams']),
    handleMessage(data) {
      const query = decodeURIComponent(data.url).split('?')[1] || '';
      switch (data.act) {
        case 'signin':
          this.setSessionParams(deparam(query));
          break;
      }
    },
    getReviewCount(index) {
      if (!this.hasEvaluationTypes) return 0;
      const reviewCountMap = this.statistics[index].evaluation_types.map(
        evaluation_type => {
          return _.sumBy(evaluation_type.options, 'count');
        }
      );
      return _.maxBy(reviewCountMap);
    },
    // https://gist.github.com/scwood/e58380174bd5a94174c9f08ac921994f
    largestRemainderRound(numbers, desiredTotal) {
      const result = numbers
        .map((number, index) => {
          return {
            floor: Math.floor(number),
            remainder: this.getRemainder(number),
            index: index
          };
        })
        .sort(function(a, b) {
          return b.remainder - a.remainder;
        });

      const lowerSum = result.reduce(function(sum, current) {
        return sum + current.floor;
      }, 0);

      const delta = desiredTotal - lowerSum;
      for (let i = 0; i < delta; i++) {
        result[i].floor++;
      }

      return result
        .sort(function(a, b) {
          return a.index - b.index;
        })
        .map(function(result) {
          return result.floor;
        });
    },
    getRemainder(number) {
      const remainder = number - Math.floor(number);
      return remainder.toFixed(4);
    }
  }
};
